/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:52:57]
Permissions to update outside the main branch: [false]
*/

import { Tooltip } from '@finbeat/react-toolkit';
import React, {
    MouseEventHandler,
    PropsWithChildren,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BsPinAngle, BsPinAngleFill } from 'react-icons/bs';
import { FiX } from 'react-icons/fi';
import { MenuItem } from 'react-pro-sidebar';
import { Link, To } from 'react-router-dom';
import './SidebarMenuItem.scss';

type SidebarMenuItemProps = {
    onClick?: MouseEventHandler<HTMLLIElement>;
    onSetFavorites?: (id: string) => void;
    id: string;
    parent?: string;
    icon: JSX.Element;
    value?: string;
    link?: To;
    isActive?: boolean;
    isSidebarCollapsed?: boolean;
    isFavorites?: boolean;
    isVisiblePin?: boolean;
    favoritMenu?: boolean;
    overflownElementClassName?: string;
};

/**
 * Возвращает MenuItem с всплывающим тултипом
 *
 * @param {JSX} item.icon отображаемая иконка
 * @param {string} item.value код для перевода
 * @param {string} item.link ссылка для навигации
 * @param {function} onClick коллбек для обработки клика
 * @returns {JSX} JSX MenuItem
 */
export default function SidebarMenuItem({
    onClick,
    onSetFavorites,
    id,
    parent,
    icon,
    value = '',
    link,
    isActive,
    isSidebarCollapsed,
    isFavorites,
    isVisiblePin = true,
    favoritMenu,
    children,
    overflownElementClassName,
}: SidebarMenuItemProps & React.PropsWithChildren) {
    const { t } = useTranslation();
    const linkRef = useRef(null);
    const itemRef = useRef(null);
    const className = 'sidebar-menu-item';
    const [isFavoritesVisibility, setFavoritesVisibility] = useState(false);

    const isPinVisible = useMemo(() => {
        return (
            favoritMenu ||
            (id !== 'logout' &&
                id !== 'login' &&
                isFavoritesVisibility &&
                !isSidebarCollapsed &&
                isVisiblePin)
        );
    }, [id, isFavoritesVisibility, isSidebarCollapsed]);

    const title = useMemo(() => {
        return (parent ? `${t(parent)}/` : '') + t(value);
    }, [t, parent, value]);

    const Content = useCallback(
        ({ children }: PropsWithChildren) => {
            return link && !isActive ? <Link to={link}>{children}</Link> : <>{children}</>;
        },
        [link, isActive]
    );

    return (
        <>
            <div
                className={className}
                onMouseEnter={() => setFavoritesVisibility(true)}
                onMouseLeave={() => setFavoritesVisibility(false)}
                ref={itemRef}
            >
                <MenuItem icon={icon} active={isActive} onClick={onClick}>
                    <Content>
                        <div
                            className={`${className}__link ${
                                isActive ? `${className}__link_active` : ''
                            }`}
                            ref={linkRef}
                        >
                            {children || title}
                        </div>
                    </Content>
                </MenuItem>
                <div
                    className={`${className}__favorites ${
                        isPinVisible ? `${className}__favorites_visible` : ''
                    }`}
                    onClick={() => id && onSetFavorites && onSetFavorites(id)}
                >
                    {favoritMenu ? <FiX /> : isFavorites ? <BsPinAngleFill /> : <BsPinAngle />}
                </div>
            </div>
            <Tooltip
                target={itemRef}
                showOnlyIfOverflownRef={linkRef}
                placement="right"
                container={itemRef}
                delay={{ show: 0, hide: 0 }}
                className={`${className}__tooltip`}
                overflownElementClassName={overflownElementClassName}
            >
                {title}
            </Tooltip>
        </>
    );
}
